import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MapPin,
  Star,
  Navigation,
  Phone,
  Plus,
  Heart,
  User,
  Shield,
  Droplets,
  Clock,
  DollarSign,
  ChevronUp,
  ChevronDown,
  Sparkles,
  LayoutDashboard,
  LocateFixed,
  Loader2,
} from "lucide-react";
import "leaflet/dist/leaflet.css";
import ReviewModal from "@/components/ReviewModal";
import RestroomDetail from "@/components/RestroomDetail";
import DirectionsModal from "@/components/DirectionsModal";
import LocationSearch from "@/components/LocationSearch";
import SOSModal from "@/components/SOSModal";
import NotificationBell from "@/components/NotificationBell";
import MapLegend from "@/components/MapLegend";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { trackEvent } from "@/lib/analytics";
import { trackError } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Category meta — color + emoji-free SVG glyph for each restroom type
const CATEGORY_META = {
  public_toilet:    { color: "#0ea5e9", label: "Public Toilet" },
  mall:             { color: "#a855f7", label: "Mall" },
  fuel:             { color: "#f97316", label: "Petrol Pump" },
  toll_plaza:       { color: "#dc2626", label: "Toll Plaza" },
  highway_services: { color: "#16a34a", label: "Highway Services" },
  restaurant:       { color: "#ec4899", label: "Restaurant" },
  hospital:         { color: "#ef4444", label: "Hospital" },
  metro_station:    { color: "#7c3aed", label: "Metro Station" },
  bus_station:      { color: "#0891b2", label: "Bus Station" },
  railway_station:  { color: "#ca8a04", label: "Railway Station" },
  other:            { color: "#64748b", label: "Other" },
};

// Custom marker icons — colored by category (with safety-rating hue applied to public toilets only)
const createMarkerIcon = (restroom) => {
  const cat = restroom?.category || "public_toilet";
  const meta = CATEGORY_META[cat] || CATEGORY_META.other;
  let color = meta.color;
  // For public toilets we tint by safety rating (community-driven)
  if (cat === "public_toilet") {
    if (restroom.safety_rating < 2.5) color = "#ef4444";
    else if (restroom.safety_rating < 3.5) color = "#f59e0b";
    else color = "#0ea5e9";
  }

  return L.divIcon({
    className: "custom-marker-container",
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: ${color};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 3px 10px rgba(0,0,0,0.2);
        border: 2px solid white;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="transform: rotate(45deg);">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Google-Maps style "you are here" indicator: a solid blue core dot with a
// soft pulsing halo when the fix is genuinely precise (real GPS lock).
// For coarse/approximate fixes (network/cell-tower or IP-based — common on
// Indian mobile carriers, often several km off) we show an honest, larger,
// non-pulsing "approximate area" circle instead of a falsely-confident dot —
// this matches how Google Maps itself distinguishes precise vs. approximate.
const PRECISE_ACCURACY_THRESHOLD_M = 1000;

const MyLocationDot = ({ position }) => {
  if (!position) return null;
  const rawAccuracy = position.accuracy || 60;
  const isPrecise = rawAccuracy <= PRECISE_ACCURACY_THRESHOLD_M;
  // Honest radius — no artificial cap that would hide real inaccuracy.
  const radius = Math.max(15, Math.min(rawAccuracy, 25000));
  return (
    <>
      <Circle
        center={[position.lat, position.lng]}
        radius={radius}
        pathOptions={{
          color: "#1a73e8",
          fillColor: "#1a73e8",
          fillOpacity: isPrecise ? 0.08 : 0.05,
          weight: 1,
          opacity: isPrecise ? 0.35 : 0.25,
          dashArray: isPrecise ? undefined : "4 4",
        }}
      />
      <Marker
        position={[position.lat, position.lng]}
        interactive={false}
        keyboard={false}
        icon={L.divIcon({
          className: "",
          html: `<div class="user-location-dot${isPrecise ? "" : " user-location-dot--approx"}" aria-hidden="true"></div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        })}
      />
    </>
  );

};

// Watches map pan/zoom and pushes the new center + radius to parent.
// Radius is clamped so runaway zoom-outs don't send huge $near queries.
const MAX_RADIUS_KM = 60;

const MapViewportTracker = ({ onViewportChange }) => {
  const map = useMapEvents({
    moveend() {
      const c = map.getCenter();
      const b = map.getBounds();
      const radiusMeters = c.distanceTo(b.getNorthEast());
      onViewportChange({
        lat: c.lat,
        lng: c.lng,
        radius: Math.min(MAX_RADIUS_KM, Math.max(2, Math.round(radiusMeters / 1000))),
      });
    },
  });
  useEffect(() => {
    const c = map.getCenter();
    const b = map.getBounds();
    const radiusMeters = c.distanceTo(b.getNorthEast());
    onViewportChange({
      lat: c.lat,
      lng: c.lng,
      radius: Math.min(MAX_RADIUS_KM, Math.max(2, Math.round(radiusMeters / 1000))),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

// Map controls component
// Google-Maps style "locate me" button: requests real GPS, updates parent state,
// flies the map to the position, with a loading spinner while in flight.
// Pick a sane zoom for the reported GPS accuracy — a coarse network/cell fix
// (often several km off, common on Indian mobile carriers before the GPS
// chip locks) must NOT be zoomed to street level; that falsely implies
// pinpoint confidence and can visually "place" the user in the wrong spot.
const zoomForAccuracy = (accuracy) => {
  if (!accuracy || accuracy <= 100) return 17;
  if (accuracy <= 500) return 16;
  if (accuracy <= 1000) return 15;
  if (accuracy <= 3000) return 13;
  if (accuracy <= 10000) return 11;
  if (accuracy <= 40000) return 9;
  return 7;
};

const MapControls = ({ onFlyTo, onLocateSuccess }) => {
  const map = useMap();
  const [locating, setLocating] = useState(false);

  // Expose flyTo method
  useEffect(() => {
    if (onFlyTo) {
      onFlyTo((lat, lng, zoom = 15) => {
        map.flyTo([lat, lng], zoom);
      });
    }
  }, [map, onFlyTo]);

  const handleLocate = () => {
    if (locating) return;
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation isn't supported on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        map.flyTo([latitude, longitude], zoomForAccuracy(accuracy), { duration: 0.8 });
        onLocateSuccess?.({ lat: latitude, lng: longitude, accuracy });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        if (err && err.code === err.PERMISSION_DENIED) {
          toast.error("Location permission denied. Enable it in your browser settings.");
        } else {
          toast.error("Couldn't get your location. Try again.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  };

  return (
    <div className="locate-me-fab">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.92 }}
        onClick={handleLocate}
        className="w-12 h-12 bg-white rounded-full shadow-lg ring-1 ring-slate-200 flex items-center justify-center text-sky-600 hover:bg-sky-50 disabled:opacity-70"
        title="Recenter on my location"
        aria-label="Recenter on my location"
        data-testid="locate-me-btn"
        disabled={locating}
      >
        {locating
          ? <Loader2 size={22} className="animate-spin" />
          : <LocateFixed size={22} strokeWidth={2.2} />}
      </motion.button>
    </div>
  );
};

export default function MapPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [restrooms, setRestrooms] = useState([]);
  const [selectedRestroom, setSelectedRestroom] = useState(null);
  const [userPosition, setUserPosition] = useState(null);
  const [viewport, setViewport] = useState({ lat: 19.076, lng: 72.8777, radius: 25 });
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [showSOS, setShowSOS] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewsRefreshKey, setReviewsRefreshKey] = useState(0);
  const [directionsOpen, setDirectionsOpen] = useState(false);
  const [directionsRoute, setDirectionsRoute] = useState(null);
  const [directionsMode, setDirectionsMode] = useState("foot");
  const [directionsLoading, setDirectionsLoading] = useState(false);
  const [directionsError, setDirectionsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noResultsModal, setNoResultsModal] = useState({ open: false, message: "" });
  const initialLoadDoneRef = useRef(false);
  const [filters, setFilters] = useState({
    minSafety: null,
    minCleanliness: null,
    type: null,
    category: null,
  });
  const mapRef = useRef(null);
  const flyToRef = useRef(null);

  // Default center (Mumbai, India) — used only if BOTH GPS and IP-lookup fail.
  const defaultCenter = [19.076, 72.8777];

  // Helper: fly the map + record the resolved location.
  const applyLocation = (lat, lng, accuracy, source, zoom = 15) => {
    setUserPosition({ lat, lng, accuracy: accuracy || null, source });
    setViewport((v) => ({ ...v, lat, lng }));
    const fly = () => {
      if (flyToRef.current) flyToRef.current(lat, lng, zoom);
      else setTimeout(fly, 250);
    };
    fly();
  };

  // Rough IP-based geolocation fallback via our backend proxy (avoids
  // browser CORS restrictions that block ipapi.co calls from the client).
  // Always trusts the backend's best-effort answer (even its own last-resort
  // "center of India" estimate) — this is still far more useful than a
  // hardcoded Mumbai default when GPS is unavailable/denied.
  const tryIpGeolocation = async () => {
    try {
      const res = await axios.get(`${API}/geocode/ip`, { timeout: 10000 });
      const { lat, lng, city, region, source, accuracy_meters } = res.data || {};
      if (typeof lat === "number" && typeof lng === "number") {
        applyLocation(lat, lng, accuracy_meters || 15000, "ip", source === "fallback" ? 6 : 12);
        toast.info(
          source === "fallback"
            ? "Couldn't detect your exact location — showing India. Tap the compass button or search to find restrooms near you."
            : `Showing ${city || region || "your approximate area"} — enable location for a precise view`,
          { duration: 5000 }
        );
        return true;
      }
    } catch (_) {
      /* ignore — will fall back to defaultCenter */
    }
    return false;
  };

  // On first mount, resolve the user's location via GPS -> IP -> default.
  // Two-phase GPS: a fast coarse fix first (typically <1s from a cached
  // position, but gets a generous window so the browser's permission
  // prompt has time to be answered), then a background high-accuracy
  // refinement. This removes the "small lag" users saw before the map
  // centred on them.
  useEffect(() => {
    let cancelled = false;

    const runHighAccuracyRefinement = () => {
      // Non-blocking follow-up to nudge the blue dot to a more precise location.
      if (!("geolocation" in navigator)) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          const { latitude, longitude, accuracy } = pos.coords;
          applyLocation(latitude, longitude, accuracy, "gps", zoomForAccuracy(accuracy));
        },
        () => { /* silent — coarse fix is already displayed */ },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 30000 }
      );
    };

    const tryGps = () =>
      new Promise((resolve) => {
        if (!("geolocation" in navigator)) return resolve(false);
        // Phase 1 — coarse & fast: accept a cached fix up to 5 minutes old.
        // Timeout is generous (10s) so a real permission-prompt interaction
        // isn't mistaken for a failure and prematurely bounced to IP fallback.
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (cancelled) return resolve(false);
            const { latitude, longitude, accuracy } = pos.coords;
            applyLocation(latitude, longitude, accuracy, "gps", zoomForAccuracy(accuracy));
            // Fire the refinement in the background (no blocking).
            runHighAccuracyRefinement();
            resolve(true);
          },
          (err) => {
            if (cancelled) return resolve(false);
            trackError("location_error", err?.message || `code=${err?.code}`, { module: "map" });
            if (err && err.code === err.PERMISSION_DENIED) {
              toast.error(
                "Location permission denied — allow in the address bar to see restrooms near you",
                { duration: 6000 }
              );
            }
            resolve(false);
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      });

    (async () => {
      const gpsOk = await tryGps();
      if (!gpsOk && !cancelled) {
        await tryIpGeolocation();
      }
      initialLoadDoneRef.current = true;
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRestrooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport.lat, viewport.lng, viewport.radius, filters]);

  const focusSearch = () => {
    const input = document.querySelector('[data-testid="location-search-input"]') || document.querySelector('input[placeholder^="Search"]');
    if (input) {
      input.focus();
      input.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Haversine — km between two lat/lng points. Cheap enough to call inline.
  const kmBetween = (a, b) => {
    if (!a || !b) return 0;
    const toRad = (v) => (v * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.asin(Math.sqrt(s));
  };

  // Fetch turn-by-turn directions from userPosition -> restroom.
  const fetchDirections = async (dest, mode = "foot") => {
    if (!dest) return;
    if (!userPosition) {
      toast.info("Enable location to get directions", {
        action: { label: "Locate me", onClick: () => document.querySelector('[data-testid="locate-me-btn"]')?.click() },
      });
      return;
    }
    setDirectionsOpen(true);
    setDirectionsLoading(true);
    setDirectionsError(null);
    setDirectionsRoute(null);
    setDirectionsMode(mode);
    try {
      const res = await axios.get(`${API}/directions`, {
        params: { from_lat: userPosition.lat, from_lng: userPosition.lng, to_lat: dest.latitude, to_lng: dest.longitude, mode },
        timeout: 15000,
      });
      setDirectionsRoute(res.data);
      trackEvent("directions_view", { module: "map", meta: { mode, distance_m: res.data.distance_m } });
      // Fit both endpoints in view.
      if (flyToRef.current && res.data.geometry?.length) {
        const midLat = (userPosition.lat + dest.latitude) / 2;
        const midLng = (userPosition.lng + dest.longitude) / 2;
        const zoom = res.data.distance_m > 5000 ? 12 : res.data.distance_m > 1500 ? 14 : 15;
        flyToRef.current(midLat, midLng, zoom);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setDirectionsError(detail || "Couldn't fetch directions right now. Please try again.");
    } finally {
      setDirectionsLoading(false);
    }
  };

  const closeDirections = () => {
    setDirectionsOpen(false);
    setDirectionsRoute(null);
    setDirectionsError(null);
  };

  const fetchRestrooms = async () => {
    try {
      const params = new URLSearchParams();
      params.append("lat", viewport.lat);
      params.append("lng", viewport.lng);
      params.append("radius", viewport.radius);
      if (filters.minSafety) params.append("min_safety", filters.minSafety);
      if (filters.minCleanliness) params.append("min_cleanliness", filters.minCleanliness);
      if (filters.type) params.append("type", filters.type);
      if (filters.category) params.append("category", filters.category);

      const response = await axios.get(`${API}/restrooms?${params.toString()}`, { timeout: 12000 });
      setRestrooms(response.data);

      // Gentle nudge if the user has panned far from their real location with
      // nothing here. Only surface this once the automatic first-load
      // geolocation has settled, and only when zoomed in close enough that
      // "no results" is a meaningful signal (not just a wide, sparse view).
      const farKm = userPosition ? kmBetween(userPosition, { lat: viewport.lat, lng: viewport.lng }) : 0;
      const zoomedInEnough = viewport.radius <= 20;
      if (
        response.data.length === 0 &&
        farKm > 60 &&
        zoomedInEnough &&
        initialLoadDoneRef.current
      ) {
        setNoResultsModal((m) => (m.open ? m : { open: true, message: "No restrooms found in this area." }));
      }
    } catch (error) {
      console.error("Error fetching restrooms:", error);
      // Graceful failure: never bark an error at the user for a network hiccup.
      if (initialLoadDoneRef.current) {
        setNoResultsModal((m) =>
          m.open ? m : { open: true, message: "Couldn't load restrooms for this area right now." }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (location) => {
    setViewport((v) => ({ ...v, lat: location.lat, lng: location.lng }));
    if (flyToRef.current) {
      flyToRef.current(location.lat, location.lng, 14);
    }
    toast.success(`Searching near ${location.name}`);
  };

  const handleSOSClick = () => {
    if (!isAuthenticated) {
      toast.error("Please login to use SOS");
      navigate("/login");
      return;
    }
    trackEvent("sos_open", { module: "sos", lat: userPosition?.lat, lng: userPosition?.lng });
    setShowSOS(true);
  };

  const handleMarkerClick = (restroom) => {
    setSelectedRestroom(restroom);
    // On desktop the sheet is a side-panel that doesn't need a peek mode; always expand.
    const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
    setSheetExpanded(isDesktop ? true : true);
  };

  const handleAddFavorite = async (restroomId) => {
    if (!isAuthenticated) {
      toast.error("Please login to save favorites");
      navigate("/login");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/favorites/${restroomId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Added to favorites!");
    } catch (error) {
      toast.error("Failed to add favorite");
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "text-green-500";
    if (rating >= 3) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <div className="h-screen w-screen overflow-hidden relative" data-testid="map-page">
      {/* Map */}
      <div className="map-container">
        <MapContainer
          center={userPosition || defaultCenter}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MyLocationDot position={userPosition} />
          {directionsRoute?.geometry?.length ? (
            <Polyline
              positions={directionsRoute.geometry}
              pathOptions={{ color: "#0ea5e9", weight: 5, opacity: 0.85 }}
            />
          ) : null}
          <MapViewportTracker onViewportChange={setViewport} />
          <MapControls
            onFlyTo={(fn) => { flyToRef.current = fn; if (typeof window !== "undefined") window.__mapFlyTo = fn; }}
            onLocateSuccess={({ lat, lng, accuracy }) => {
              setUserPosition({ lat, lng, accuracy: accuracy || null, source: "gps" });
              setViewport((v) => ({ ...v, lat, lng }));
              trackEvent("locate_me", { module: "map", lat, lng });
            }}
          />
          
          {restrooms.map((restroom) => (
            <Marker
              key={restroom.restroom_id}
              position={[restroom.latitude, restroom.longitude]}
              icon={createMarkerIcon(restroom)}
              eventHandlers={{
                click: () => handleMarkerClick(restroom),
              }}
            >
              <Popup>
                <div className="p-2">
                  <h3 className="font-semibold text-sm">{restroom.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Shield size={14} className={getRatingColor(restroom.safety_rating)} />
                    <span className="text-xs">{restroom.safety_rating?.toFixed(1) ?? "3.0"}</span>
                    <Droplets size={14} className={getRatingColor(restroom.cleanliness_rating)} />
                    <span className="text-xs">{restroom.cleanliness_rating?.toFixed(1) ?? "3.0"}</span>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Search Bar + Category Filter */}
      <div className="search-overlay">
        <div className="flex flex-col gap-2 max-w-lg mx-auto">
          <LocationSearch 
            onLocationSelect={handleLocationSelect}
            placeholder="Search locations in India..."
          />
          <div
            className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar"
            data-testid="category-filter-bar"
          >
            {[
              { key: null, testid: "all", label: "All", color: "#0f172a" },
              { key: "public_toilet", testid: "public_toilet", label: "Public Toilets", color: "#0ea5e9" },
              { key: "mall", testid: "mall", label: "Malls", color: "#a855f7" },
              { key: "fuel", testid: "fuel", label: "Petrol Pumps", color: "#f97316" },
              { key: "toll_plaza", testid: "toll_plaza", label: "Toll Plazas", color: "#dc2626" },
              { key: "highway_services", testid: "highway_services", label: "Highway Services", color: "#16a34a" },
              { key: "restaurant", testid: "restaurant", label: "Restaurants", color: "#ec4899" },
              { key: "hospital", testid: "hospital", label: "Hospitals", color: "#ef4444" },
              { key: "bus_station,metro_station,railway_station", testid: "transit", label: "Transit (Bus/Rail)", color: "#0f766e" },
              { key: "metro_station", testid: "metro_station", label: "Metro", color: "#7c3aed" },
              { key: "bus_station", testid: "bus_station", label: "Bus", color: "#0891b2" },
              { key: "railway_station", testid: "railway_station", label: "Railway Station", color: "#ca8a04" },
            ].map((c) => {
              const active = filters.category === c.key;
              return (
                <button
                  key={c.label}
                  onClick={() => setFilters((f) => ({ ...f, category: c.key }))}
                  data-testid={`category-chip-${c.testid}`}
                  className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all shadow-sm ${
                    active
                      ? "text-white"
                      : "bg-white/95 text-slate-700 hover:bg-white border border-slate-200"
                  }`}
                  style={active ? { backgroundColor: c.color } : {}}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* SOS Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleSOSClick}
        className={`fixed top-20 right-4 z-20 w-14 h-14 btn-sos rounded-full flex items-center justify-center ${showSOS ? "sos-pulse" : ""}`}
        data-testid="sos-button"
      >
        <Phone size={24} className="text-white" />
      </motion.button>

      <SOSModal open={showSOS} onClose={() => setShowSOS(false)} isAuthenticated={isAuthenticated} />

      <NotificationBell token={localStorage.getItem("token")} isAuthenticated={isAuthenticated} />

      <MapLegend
        categories={Object.entries(CATEGORY_META)
          .filter(([key]) => key !== "other")
          .map(([key, meta]) => ({ key, ...meta }))}
      />

      {/* No-results / fetch-error modal — explicit dismiss, no auto-timeout toast */}
      <AlertDialog open={noResultsModal.open} onOpenChange={(open) => setNoResultsModal((m) => ({ ...m, open }))}>
        <AlertDialogContent data-testid="no-results-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Nothing here yet</AlertDialogTitle>
            <AlertDialogDescription>
              {noResultsModal.message} Try searching for a city or landmark instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="no-results-modal-close">Close</AlertDialogCancel>
            <AlertDialogAction
              data-testid="no-results-modal-search"
              onClick={() => setTimeout(focusSearch, 150)}
            >
              Search manually
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* About / Support button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate("/about")}
        className="fixed top-20 right-20 z-20 w-12 h-12 bg-white border border-slate-200 rounded-full shadow-md flex items-center justify-center text-rose-600 hover:bg-rose-50"
        title="About & Support"
        data-testid="about-button"
      >
        <Heart size={20} />
      </motion.button>

      {/* Floating Action Buttons */}
      <div
        className="fab-container"
        style={{
          bottom:
            sheetExpanded && typeof window !== "undefined" && window.innerWidth < 1024
              ? "calc(70vh + 88px)"
              : "160px",
        }}
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/add-restroom")}
          className="w-14 h-14 bg-sky-600 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-sky-700"
          data-testid="add-restroom-btn"
        >
          <Plus size={24} />
        </motion.button>
      </div>

      {/* Bottom Sheet (mobile drawer) / right-side detail panel (desktop) */}
      <AnimatePresence>
        {(selectedRestroom || sheetExpanded) && (
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bottom-sheet"
            data-sheet-expanded={sheetExpanded ? "1" : "0"}
            style={{
              height: sheetExpanded
                ? "min(70vh, calc(100vh - 96px))"
                : "220px",
            }}
          >
            <div
              className="cursor-pointer flex-shrink-0 drag-handle-desktop-hide"
              onClick={() => setSheetExpanded(!sheetExpanded)}
            >
              <div className="bottom-sheet-handle" />
              <div className="flex justify-center pb-2">
                {sheetExpanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
              </div>
            </div>

            <ScrollArea className="sheet-scroll px-4 pb-6">
              {selectedRestroom ? (
                <RestroomDetail
                  restroom={selectedRestroom}
                  refreshKey={reviewsRefreshKey}
                  onClose={() => {
                    setSelectedRestroom(null);
                    setSheetExpanded(false);
                  }}
                  onReview={() => setShowReviewModal(true)}
                  onFavorite={() => handleAddFavorite(selectedRestroom.restroom_id)}
                  onDirections={() => fetchDirections(selectedRestroom, directionsMode)}
                  getRatingColor={getRatingColor}
                />
              ) : (
                <div>
                  <h2 className="text-lg font-bold mb-4">Nearby Restrooms</h2>
                  {loading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="skeleton h-20 rounded-xl" />
                      ))}
                    </div>
                  ) : restrooms.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No restrooms found nearby</p>
                  ) : (
                    <div className="space-y-3">
                      {restrooms.slice(0, 10).map((restroom) => (
                        <motion.div
                          key={restroom.restroom_id}
                          whileHover={{ scale: 1.01 }}
                          onClick={() => handleMarkerClick(restroom)}
                          className="restroom-card bg-white p-4 rounded-xl border border-slate-100 cursor-pointer"
                          data-testid={`restroom-card-${restroom.restroom_id}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-900">{restroom.name}</h3>
                              <p className="text-sm text-slate-500 mt-1">{restroom.address}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center gap-1">
                                <Shield size={14} className={getRatingColor(restroom.safety_rating)} />
                                <span className="text-sm font-medium">{restroom.safety_rating?.toFixed(1) ?? "3.0"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Droplets size={14} className={getRatingColor(restroom.cleanliness_rating)} />
                                <span className="text-sm font-medium">{restroom.cleanliness_rating?.toFixed(1) ?? "3.0"}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {restroom.category && CATEGORY_META[restroom.category] && (
                              <Badge
                                className="text-xs text-white"
                                style={{ backgroundColor: CATEGORY_META[restroom.category].color }}
                              >
                                {CATEGORY_META[restroom.category].label}
                              </Badge>
                            )}
                            {restroom.type === "paid" && (
                              <Badge variant="secondary" className="text-xs">
                                <DollarSign size={12} className="mr-1" />
                                Paid
                              </Badge>
                            )}
                            {restroom.is_24_hours && (
                              <Badge variant="secondary" className="text-xs">
                                <Clock size={12} className="mr-1" />
                                24h
                              </Badge>
                            )}
                            {restroom.verified && (
                              <Badge className="text-xs bg-green-100 text-green-700">
                                <Sparkles size={12} className="mr-1" />
                                Verified
                              </Badge>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Modal */}
      {showReviewModal && selectedRestroom && (
        <ReviewModal
          restroom={selectedRestroom}
          onClose={() => setShowReviewModal(false)}
          onSubmit={async () => {
            setShowReviewModal(false);
            // Trigger RestroomDetail to refetch reviews (+ aggregate ratings)
            setReviewsRefreshKey((k) => k + 1);
            // Refresh the currently selected restroom so ratings/total_reviews update immediately
            try {
              const fresh = await axios.get(`${API}/restrooms/${selectedRestroom.restroom_id}`);
              setSelectedRestroom(fresh.data);
              setRestrooms((prev) =>
                prev.map((r) => (r.restroom_id === fresh.data.restroom_id ? fresh.data : r))
              );
            } catch (_) {
              // fall back to a full refetch
              fetchRestrooms();
            }
          }}
        />
      )}

      {/* Directions Modal */}
      <DirectionsModal
        isOpen={directionsOpen}
        onClose={closeDirections}
        restroom={selectedRestroom}
        route={directionsRoute}
        loading={directionsLoading}
        error={directionsError}
        mode={directionsMode}
        onModeChange={(m) => selectedRestroom && fetchDirections(selectedRestroom, m)}
      />

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <div className="flex justify-around items-center h-16 max-w-md mx-auto">
          <button
            onClick={() => {
              setSelectedRestroom(null);
              setSheetExpanded(true);
            }}
            className="flex flex-col items-center gap-1 text-sky-600"
            data-testid="nav-map"
          >
            <MapPin size={22} />
            <span className="text-xs font-medium">Map</span>
          </button>
          <button
            onClick={() => isAuthenticated ? navigate("/favorites") : navigate("/login")}
            className="flex flex-col items-center gap-1 text-slate-500 hover:text-sky-600"
            data-testid="nav-saved"
          >
            <Heart size={22} />
            <span className="text-xs font-medium">Saved</span>
          </button>
          <button
            onClick={() => isAuthenticated ? navigate("/add-restroom") : navigate("/login")}
            className="flex flex-col items-center gap-1 text-slate-500 hover:text-sky-600"
            data-testid="nav-contribute"
          >
            <Plus size={22} />
            <span className="text-xs font-medium">Add</span>
          </button>
          {isAuthenticated ? (
            <>
              {user?.role === "admin" && (
                <button
                  onClick={() => navigate("/admin")}
                  className="flex flex-col items-center gap-1 text-slate-500 hover:text-sky-600"
                  data-testid="nav-admin"
                >
                  <LayoutDashboard size={22} />
                  <span className="text-xs font-medium">Admin</span>
                </button>
              )}
              <button
                onClick={() => navigate("/profile")}
                className="flex flex-col items-center gap-1 text-slate-500 hover:text-sky-600"
                data-testid="nav-profile"
              >
                <User size={22} />
                <span className="text-xs font-medium">Profile</span>
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="flex flex-col items-center gap-1 text-slate-500 hover:text-sky-600"
              data-testid="nav-login"
            >
              <User size={22} />
              <span className="text-xs font-medium">Login</span>
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}
